/**
 * Angle Role - Format Pillar
 *
 * Formatação de valores de ângulo para exibição.
 *
 * @example
 * import { formatAngle, getAngleSymbol, getAngleName } from '@attrx/role-morphic/angle/format';
 *
 * formatAngle('degree', 45);                     // "45°"
 * formatAngle('degree', 45, { verbose: true });  // "45 degrees"
 * getAngleSymbol('degree');                      // "°"
 * getAngleName('degree', 2);                     // "degrees"
 */

import { ANGLE_UNITS, type AngleUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export interface AngleFormatOptions {
  /** Número de casas decimais */
  decimals?: number;
  /** Usar nome completo ao invés de símbolo */
  verbose?: boolean;
  /** Locale para formatação numérica */
  locale?: string;
  /** Notação: standard, scientific, compact */
  notation?: "standard" | "scientific" | "compact";
}

// =============================================================================
// FORMAT
// =============================================================================

/**
 * Formata valor de ângulo para exibição
 *
 * @param variant - Unidade
 * @param value - Valor numérico
 * @param options - Opções de formatação
 * @returns String formatada
 *
 * @example
 * formatAngle('degree', 45);                     // "45°"
 * formatAngle('degree', 45, { verbose: true });  // "45 degrees"
 * formatAngle('radian', 3.14159);                // "3.14159 rad"
 * formatAngle('degree', 90, { decimals: 0 });    // "90°"
 */
export function formatAngle(
  variant: AngleUnit,
  value: number,
  options: AngleFormatOptions = {},
): string {
  const config = ANGLE_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown angle unit: ${variant}`);
  }

  const { decimals, verbose = false, locale, notation = "standard" } = options;

  // Format number
  let formattedNumber: string;

  if (locale || notation !== "standard") {
    const intlOptions: Intl.NumberFormatOptions = { notation };
    if (decimals !== undefined) {
      intlOptions.minimumFractionDigits = decimals;
      intlOptions.maximumFractionDigits = decimals;
    }
    formattedNumber = new Intl.NumberFormat(locale, intlOptions).format(value);
  } else if (decimals !== undefined) {
    formattedNumber = value.toFixed(decimals);
  } else {
    // Smart formatting: remove trailing zeros
    formattedNumber = Number(value.toPrecision(12)).toString();
  }

  // Get unit label
  const unitLabel = verbose
    ? value === 1
      ? config.singular
      : config.plural
    : config.symbol;

  // Some angle units don't use space (e.g., 45° not 45 °)
  const separator = config.noSpace && !verbose ? "" : " ";

  return `${formattedNumber}${separator}${unitLabel}`;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Retorna o símbolo de uma unidade
 *
 * @example
 * getAngleSymbol('degree');  // "°"
 * getAngleSymbol('radian');  // "rad"
 */
export function getAngleSymbol(variant: AngleUnit): string {
  const config = ANGLE_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown angle unit: ${variant}`);
  }
  return config.symbol;
}

/**
 * Retorna o nome de uma unidade (singular ou plural)
 *
 * @param variant - Unidade
 * @param count - Quantidade para determinar singular/plural (default: 1)
 *
 * @example
 * getAngleName('degree', 1);  // "degree"
 * getAngleName('degree', 2);  // "degrees"
 */
export function getAngleName(variant: AngleUnit, count: number = 1): string {
  const config = ANGLE_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown angle unit: ${variant}`);
  }
  return count === 1 ? (config.singular || config.symbol) : (config.plural || config.symbol);
}
